/**
 * Pausa na partida (F80, ui-h à esquerda): quando o Esc abre ou fecha o menu, e o aviso de que o mundo não
 * para (a partida é do servidor, inclusive sozinho). Puro, para os testes em node.
 */

/**
 * Depois de o ponteiro soltar, o Esc que o soltou ainda pode chegar: nesse intervalo ele não abre a pausa
 * nem fecha a que a soltura abriu (F99).
 */
export const UNLOCK_GRACE_MS = 300;

export interface EscapeState {
  /** O menu de pausa está aberto. */
  pauseOpen: boolean;
  /** Um painel que fecha no próprio Esc está aberto (mapa, chat, Opções, ficha): o Esc é dele. */
  otherOpen: boolean;
  /** Um painel com Esc próprio (Opções, mapa, ficha) está aberto com o foco fora dele (o Esc dele não chega): a cena fecha. */
  settingsUnfocused?: boolean;
  /** Um painel sem Esc próprio está aberto (inventário, fabricação, mascate, Barco, Bênçãos): a cena fecha. */
  plainOpen?: boolean;
  /** O ponteiro está travado no jogo (o navegador usa o Esc para soltá-lo). */
  locked: boolean;
  /** Tempo desde que o ponteiro soltou, em ms (`Infinity` se nunca travou). */
  sinceUnlockMs: number;
}

export type EscapeAction = "abrir" | "fechar" | "fechar-opcoes" | "fechar-painel" | "nada";

/**
 * O que o Esc faz na partida, do painel de cima para baixo. As Opções com o foco fora delas fecham pela cena;
 * os painéis com Esc próprio ficam com ele; a pausa aberta fecha; um painel sem Esc próprio fecha; com o
 * ponteiro travado, o Esc é do navegador (solta o mouse, e a soltura abre a pausa: F99); com o ponteiro livre
 * e nada aberto, abre.
 */
export function escapeAction(s: EscapeState): EscapeAction {
  if (s.settingsUnfocused) return "fechar-opcoes";
  if (s.otherOpen) return "nada";
  if (s.pauseOpen) return s.sinceUnlockMs < UNLOCK_GRACE_MS ? "nada" : "fechar";
  if (s.plainOpen) return "fechar-painel";
  if (s.locked || s.sinceUnlockMs < UNLOCK_GRACE_MS) return "nada";
  return "abrir";
}

/**
 * Subtítulo da pausa: o jogo não para, nem sozinho. `others` é quantos outros jogadores estão na sala; na
 * Treta (`rivals`), eles são rivais, não aliados.
 */
export function pauseNote(others: number, rivals = false): string {
  if (others <= 0) return "O mundo não para: a noite chega mesmo com o menu aberto.";
  const who = rivals ? (others === 1 ? "seu rival segue" : `seus ${others} rivais seguem`) : others === 1 ? "seu aliado segue" : `seus ${others} aliados seguem`;
  return `O mundo não para: ${who} jogando.`;
}

/** Rótulo do botão de sair: o primeiro clique pede confirmação (sair não tem volta). */
export const leaveLabel = (confirming: boolean): string => (confirming ? "Sair mesmo? Clique de novo" : "Sair da partida");
